import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./game.css";
import Swal from "sweetalert2";
import { updateScore, alreadyPlayed, getScore } from "../../scoreservice";

function DreamcatcherGame({ updateHeader }) {
  const navigate = useNavigate();

  const [themeData, setThemeData] = useState(null);
  const [rules, setRules] = useState([]);
  const [words, setWords] = useState([]); // [{ id, text, type: 'positive'|'negative' }]
  const [positiveWords, setPositiveWords] = useState([]); // legacy (non-portrait checks)
  const [positions, setPositions] = useState([]);
  const [points, setPoints] = useState(0);
  const [counter, setCounter] = useState(0);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [riddlerusLogo, setRiddlerusLogo] = useState("/img/logo.png");
  const [isPortrait, setIsPortrait] = useState(false);

  const [isLogoSet, SetIsLogoSet] = useState(false);
  const [timeLeft, setTimeLeft] = useState(45);
  const [elapsedTime, setElapsedTime] = useState(0); 
  const [gameComplete, setGameComplete] = useState(false);
  const [fx, setFx] = useState(null); // { type: 'correct'|'wrong', text?: string, nonce: number }

  const dropRef = useRef(null);
  const headerSet = useRef(false);
  const fxTimeoutRef = useRef(null);
  const portraitPoolRef = useRef({ positive: [], negative: [] });

  const S3_URL = (process.env.REACT_APP_S3_PATH || "").replace(/\/*$/, "/");
  const S3_bucket_URL = process.env.REACT_APP_S3BUCKET_URL;
  const storedUserData =
    JSON.parse(sessionStorage.getItem("userData")) || {};
  
  useEffect(() => {

  if (!storedUserData?.sessionId || isDemoBypass) return;

  const checkExistingScore = async () => {

    try {

      const response = await getScore({
        userId: storedUserData.userId || storedUserData.userid,
        sessionId: storedUserData.sessionId,
        organizationId: storedUserData.organizationId
      });

      console.log("Initial getScore:", response);

      if (response?.status === "success") {

        const dbTime = response?.data?.time;
        const normalizedTime = String(dbTime).trim();

        // 🔥 Navigate if time is NOT zero
        const isZeroTime =
          normalizedTime === "00:00" ||
          normalizedTime === "0" ||
          normalizedTime === "00:00:00" ||
          normalizedTime === "0:00";

        if (!isZeroTime) {

          navigate("/thankyou", {
            replace: true,
            state: {
              points: response.data.total_score,
              time: dbTime
            }
          });

        }

      }

    } catch (error) {
      console.error("Initial score check error:", error);
    }

  };

  checkExistingScore();

}, []);

  const bgRef = useRef(null);
  const imageRefs = useRef([]);
  const dragRefs = useRef([]);
  const textRefs = useRef([]);
  const pointsRef = useRef(0);

  // Store the currently dragged element
  const [draggedElement, setDraggedElement] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const touchIdentifierRef = useRef(null);
  const dragThreshold = 5; // pixels - only start drag after moving past this
  const pendingDragRef = useRef(null); // { element, index, offsetX, offsetY, startX, startY }


  const STORAGE_KEY = "gameState_v1"; // key for autosave

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);

  // ---- Helpers ----
  const handleSuccess = (msg) => {
    return Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: msg,
      confirmButtonText: 'OK',
      customClass: { popup: 'custom-success-popup' }
    }).then(result => {
      return result; 
    });
  };

  const handleError = (msg) => {
    Swal.fire({
      icon: 'error',
      title: 'Oops...',
      text: msg,
      customClass: { popup: 'custom-success-popup' }
    });
  };

  function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  const isDemoBypass =
    String(storedUserData?.sessionId).toLowerCase() === "demobypass" &&
    String(storedUserData?.organizationId).toLowerCase() === "demobypass";

  useEffect(() => {
    const mq = window.matchMedia(
      "(max-width: 940px) and (orientation: portrait)"
    );
    const update = () => setIsPortrait(!!mq.matches);
    update();

    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (fxTimeoutRef.current) clearTimeout(fxTimeoutRef.current);
    };
  }, []);

  const triggerFx = (nextFx, durationMs = 750) => {
    if (fxTimeoutRef.current) clearTimeout(fxTimeoutRef.current);
    setFx({ ...nextFx, nonce: Date.now() + Math.random() });
    fxTimeoutRef.current = setTimeout(() => setFx(null), durationMs);
  };

  const getDropCenter = () => {
    const rect = dropRef.current?.getBoundingClientRect?.();
    if (!rect) return null;
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
  };

  const pickRandomSplashWord = (sourceText) => {
    const fallback = ["Wow", "Amazing", "Great"];
    const raw = String(sourceText || "").trim();
    if (!raw) return fallback[Math.floor(Math.random() * fallback.length)];

    const parts = raw
      .split(/[\n,|;/]+/g)
      .map((s) => String(s).trim())
      .filter(Boolean);

    const pool = parts.length ? parts : fallback;
    return pool[Math.floor(Math.random() * pool.length)];
  };

  const pickRandomSplashColor = () => {
    const palette = [
      "#FF2D95",
      "#7CFF6B",
      "#39B6FF",
      "#FFD54A",
      "#B56CFF",
      "#FF6B3D"
    ];
    return palette[Math.floor(Math.random() * palette.length)];
  };

  const openHowToPlay = () => {
    const safeRules = Array.isArray(rules) ? rules.filter(Boolean) : [];
    const html =
      safeRules.length > 0
        ? `<ol style="text-align:left; padding-left: 18px; margin: 0;">${safeRules
            .map((r) => `<li style="margin: 6px 0;">${String(r)}</li>`)
            .join("")}</ol>`
        : `<div style="text-align:center;">No rules available.</div>`;

    Swal.fire({
      title: "How to play",
      html,
      confirmButtonText: "Got it",
      confirmButtonColor: themeData?.button_color || "#0ea5e9",
      color: themeData?.landing_page_title_color || "#111111",
      customClass: {
        popup: "custom-success-popup rules-swal-popup",
        confirmButton: "rules-swal-confirm"
      },
      didOpen: (popup) => {
        const bgUrl = `${S3_URL}${themeData?.all_icon_image || ""}`;
        if (themeData?.all_icon_image && popup) {
          // Use a blurred background layer via CSS ::before (keeps text crisp)
          popup.style.setProperty("--rules-bg-url", `url("${bgUrl}")`);
        }

        if (popup) {
          const bodyColor =
            themeData?.icon_color ||
            themeData?.landing_page_title_color ||
            "#ffffff";

          const btnTextColor =
            themeData?.button_text_color ||
            themeData?.buton_text_color ||
            themeData?.buttonTextColor ||
            themeData?.landing_page_title_color ||
            "#ffffff";

          // Title + confirm button text
          popup.style.setProperty("--rules-title-color", btnTextColor);
          popup.style.setProperty("--rules-btn-text-color", btnTextColor);

          // Inner rules text
          popup.style.setProperty("--rules-body-color", bodyColor);
        }
      }
    });
  };


  const completeGame = async ({ byTimeout = false } = {}) => {
    if (gameComplete) return;   

    setGameComplete(true);

    const currentPoints = pointsRef.current;

    try {
      if (!isDemoBypass) {
        const shouldStoreThemeTimerAsPlayed =
          byTimeout &&
          themeData?.use_timeout === "1" &&
          String(themeData?.timer || "").includes(":");

        const timeToStore = formatTime(); // keep existing DB `time` behavior
        const timePlayedToStore = shouldStoreThemeTimerAsPlayed
          ? String(themeData.timer).trim()
          : undefined;

        await updateScore({
          userId: storedUserData.userId || storedUserData.userid,
          sessionId: storedUserData.sessionId,
          organizationId: storedUserData.organizationId,
          points: currentPoints,
          time: timeToStore,
          ...(timePlayedToStore ? { time_played: timePlayedToStore } : {})
        });
      }

    } catch (err) {
      console.error("Score update failed:", err);
    }


    navigate("/thankyou", {
      state: {
        points: currentPoints,
        time: formatTime()
      }
    });
  };



  useEffect(() => {
    if (headerSet.current) return;

    updateHeader({
      backButtonRedirect: "/login?save=true"
    });

    headerSet.current = true;
  }, [updateHeader]);

  const applyCloudTransform = () => {
    const bgEl = bgRef.current;
    if (!bgEl) return;

    const bgRect = bgEl.getBoundingClientRect();
    const bgCenterX = bgRect.left + bgRect.width / 2;

    Object.keys(imageRefs.current).forEach((key) => {
      const imgEl = imageRefs.current[key];
      if (!imgEl) return;

      const cloudEl = dragRefs.current[key];
      if (!cloudEl) return;

      // If the cloud is currently in a drag/snap cycle, keep its flip locked
      const locked = cloudEl.dataset?.flipLock;
      if (locked === "-1" || locked === "1") {
        imgEl.style.setProperty("--cloud-flip", locked);
        return;
      }

      const cloudRect = cloudEl.getBoundingClientRect();
      const cloudCenterX = cloudRect.left + cloudRect.width / 2;

      // Use CSS var so animations keep the flip
      imgEl.style.setProperty(
        "--cloud-flip",
        cloudCenterX < bgCenterX ? "-1" : "1"
      );
    });
  };

  const resizeCloudsToText = () => {
    const textMap = textRefs.current || {};
    const imgMap = imageRefs.current || {};

    Object.keys(textMap).forEach((key) => {
      const textEl = textMap[key];
      const imgEl = imgMap[key];
      if (!textEl || !imgEl) return;

      const rect = textEl.getBoundingClientRect();
      if (!rect.width) return;

      const padding = 40; 
      const minWidth = 120;
      const maxWidth = 260;

      const newWidth = Math.min(
        maxWidth,
        Math.max(minWidth, rect.width + padding)
      );

      imgEl.style.width = `${newWidth}px`;
    });
  };

  useEffect(() => {
    const bgEl = bgRef.current;
    if (!bgEl) return;

    const handleLoad = () => {
      setTimeout(() => {
        resizeCloudsToText();
        applyCloudTransform();
      }, 100);
    };

    if (bgEl.complete) {
      handleLoad();
    } else {
      bgEl.addEventListener("load", handleLoad);
      return () => bgEl.removeEventListener("load", handleLoad);
    }
  }, []);

  useEffect(() => {
    if (words.length > 0 && positions.length > 0) {
      setTimeout(() => {
        resizeCloudsToText();
        applyCloudTransform();
      }, 100);
    }
  }, [words, positions]);

 

  useEffect(() => {

    const storedTheme = sessionStorage.getItem("themeData");

    if (!storedTheme) {
      navigate("/login");
      return;
    }

    const parsedTheme = JSON.parse(storedTheme);

    setThemeData(parsedTheme);
    setRules(parsedTheme.rules || []);

    const positive = parsedTheme.default_cat || [];
    const negative = parsedTheme.negative_cat || [];

    const totalCount =
      parseInt(parsedTheme.category_upload_count) || 0;

    // Clamp requested counts to the actual array lengths
    const positiveCountRaw =
      parseInt(parsedTheme.positive_upload_count) || 0;

    let negativeCountRaw =
      parseInt(parsedTheme.negtive_upload_count);

    if (!negativeCountRaw || negativeCountRaw <= 0) {
      negativeCountRaw = totalCount - positiveCountRaw;
    }

    const positiveCount = Math.min(positiveCountRaw, positive.length);
    const negativeCount = Math.min(negativeCountRaw, negative.length);

    console.log("Positive words:", positive, `(have ${positive.length}, need ${positiveCountRaw})`);
    console.log("Negative words:", negative, `(have ${negative.length}, need ${negativeCountRaw})`);

    // Block game if required words not available
    if (positiveCount < positiveCountRaw || negativeCount < negativeCountRaw) {
      Swal.fire({
        icon: 'warning',
        title: 'Insufficient Words',
        text: 'Please check with admin',
        confirmButtonText: 'OK',
        customClass: { popup: 'custom-success-popup' }
      }).then(() => {
        navigate("/game", { replace: true });
      });
      return;
    }

    const shouldShuffle =
      String(parsedTheme.shuffle) === "1";

    const shuffleArray = (arr) =>
      [...arr].sort(() => Math.random() - 0.5);

    let selectedPositive = shouldShuffle
      ? shuffleArray(positive).slice(0, positiveCount)
      : positive.slice(0, positiveCount);

    let selectedNegative = shouldShuffle
      ? shuffleArray(negative).slice(0, negativeCount)
      : negative.slice(0, negativeCount);

    let merged = [...selectedPositive, ...selectedNegative];

    if (shouldShuffle) {
      merged = shuffleArray(merged);
    }

    const formattedPositive = selectedPositive.map((word, i) => ({
      id: `pos-${i}-${Date.now()}-${Math.random()}`,
      text: word,
      type: "positive"
    }));

    const formattedNegative = selectedNegative.map((word, i) => ({
      id: `neg-${i}-${Date.now()}-${Math.random()}`,
      text: word,
      type: "negative"
    }));

    const formattedWords = merged.map((word, i) => ({
      id: `word-${i}-${Date.now()}-${Math.random()}`,
      text: word,
      type: selectedPositive.includes(word) ? "positive" : "negative"
    }));

    console.log("Dreamcatcher words count:", {
      totalMerged: merged.length,
      positiveRequested: positiveCountRaw,
      negativeRequested: negativeCountRaw,
      positiveUsed: selectedPositive.length,
      negativeUsed: selectedNegative.length
    });

    setPositiveWords(selectedPositive);
    if (isPortrait) {
      const posPool = [...formattedPositive];
      const negPool = [...formattedNegative];

      const take = (arr, n) => arr.splice(0, Math.max(0, n));

      const active = [
        ...take(posPool, 3),
        ...take(negPool, 3)
      ].filter(Boolean);

      // If not enough on either side, fill up to 6 with what exists (still capped at 6)
      while (active.length < 6 && (posPool.length || negPool.length)) {
        if (negPool.length) active.push(negPool.shift());
        else if (posPool.length) active.push(posPool.shift());
      }

      const slots = new Array(6).fill(null);
      for (let i = 0; i < Math.min(6, active.length); i++) {
        slots[i] = active[i];
      }

      portraitPoolRef.current = { positive: posPool, negative: negPool };

      setWords(slots);
      setPositions(
        slots.map(() => ({
          left: 0,
          top: 0
        }))
      );
    } else {
      setWords(formattedWords);
      setPositions(
        formattedWords.map(() => ({
          left: 0,
          top: 0
        }))
      );
    }

  }, [navigate, isPortrait]);



  useEffect(() => {
    if (!themeData) return;

    if (themeData.use_timeout === "1") {
      const [min, sec] =
        themeData.timer.trim().split(":").map(Number);

      setTimeLeft(min * 60 + sec);
      setElapsedTime(0); 
    } else {
      setTimeLeft(0);
      setElapsedTime(0); 
    }
  }, [themeData]);

  // Timer countdown when timeout is enabled
  useEffect(() => {
    if (themeData?.use_timeout !== "1") return;
    if (gameComplete) return;
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          if (!isDemoBypass) completeGame({ byTimeout: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, gameComplete]);

  // Timer countup when timeout is disabled
  useEffect(() => {
    if (themeData?.use_timeout === "1") return;
    if (gameComplete) return;

    const timer = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [themeData?.use_timeout, gameComplete]);


  const formatTime = () => {
    if (themeData?.use_timeout !== "1") {
      // Count up: show elapsed time
      const min = Math.floor(elapsedTime / 60);
      const sec = elapsedTime % 60;
      return `${min.toString().padStart(2, "0")}:${sec
        .toString()
        .padStart(2, "0")}`;
    }

    // Count down: show remaining time
    const min = Math.floor(timeLeft / 60);
    const sec = timeLeft % 60;

    return `${min.toString().padStart(2, "0")}:${sec
      .toString()
      .padStart(2, "0")}`;
  };


  const handleMouseDown = (e, index) => {
    e.preventDefault();
    const wordObj = words[index];
    if (!wordObj) return;

    const element = dragRefs.current[wordObj.id];
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const startX = e.clientX;
    const startY = e.clientY;

    const handlePendingMouseMove = (moveE) => {
      const dx = moveE.clientX - startX;
      const dy = moveE.clientY - startY;
      if (Math.sqrt(dx * dx + dy * dy) >= dragThreshold) {
        document.removeEventListener('mousemove', handlePendingMouseMove);
        document.removeEventListener('mouseup', handlePendingMouseUp);
        startDrag(element, index, offsetX, offsetY, rect);
      }
    };

    const handlePendingMouseUp = () => {
      document.removeEventListener('mousemove', handlePendingMouseMove);
      document.removeEventListener('mouseup', handlePendingMouseUp);
      pendingDragRef.current = null;
    };

    pendingDragRef.current = { element, index };
    document.addEventListener('mousemove', handlePendingMouseMove);
    document.addEventListener('mouseup', handlePendingMouseUp);
  };


  const handleTouchStart = (e, index) => {
    e.preventDefault();

    const touch = e.touches[0];
    if (!touch) return;

    const wordObj = words[index];
    if (!wordObj) return;

    const element = dragRefs.current[wordObj.id];
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const offsetX = touch.clientX - rect.left;
    const offsetY = touch.clientY - rect.top;
    const startX = touch.clientX;
    const startY = touch.clientY;
    const touchId = touch.identifier;

    const handlePendingTouchMove = (moveE) => {
      const t = Array.from(moveE.touches).find(touch => touch.identifier === touchId);
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Math.sqrt(dx * dx + dy * dy) >= dragThreshold) {
        document.removeEventListener('touchmove', handlePendingTouchMove, { passive: false });
        document.removeEventListener('touchend', handlePendingTouchEnd, { passive: false });
        document.removeEventListener('touchcancel', handlePendingTouchEnd, { passive: false });
        touchIdentifierRef.current = touchId;
        startDrag(element, index, offsetX, offsetY, rect);
      }
    };

    const handlePendingTouchEnd = () => {
      document.removeEventListener('touchmove', handlePendingTouchMove, { passive: false });
      document.removeEventListener('touchend', handlePendingTouchEnd, { passive: false });
      document.removeEventListener('touchcancel', handlePendingTouchEnd, { passive: false });
      pendingDragRef.current = null;
    };

    pendingDragRef.current = { element, index };
    document.addEventListener('touchmove', handlePendingTouchMove, { passive: false });
    document.addEventListener('touchend', handlePendingTouchEnd, { passive: false });
    document.addEventListener('touchcancel', handlePendingTouchEnd, { passive: false });
  };

  const startDrag = (element, index, offsetX, offsetY, rect) => {
    setDraggedElement({ element, index });
    setDragOffset({ x: offsetX, y: offsetY });
    setIsDragging(true);

    // Lock current flip so it doesn't suddenly mirror when it snaps back
    const wordObj = words[index];
    if (wordObj?.id) {
      const imgEl = imageRefs.current[wordObj.id];
      const currentFlip =
        (imgEl && imgEl.style.getPropertyValue("--cloud-flip")) ||
        (imgEl && window.getComputedStyle(imgEl).getPropertyValue("--cloud-flip")) ||
        "-1";
      element.dataset.flipLock = String(currentFlip).trim() || "-1";
    }

    element.classList.add('dragging');

    element.style.position = 'fixed';
    element.style.zIndex = '1000';
    element.style.left = `${rect.left}px`;
    element.style.top = `${rect.top}px`;
    element.style.transformOrigin = `${offsetX}px ${offsetY}px`;
    element.style.transform = 'scale(1.1)';
    element.style.cursor = 'grabbing';
    element.style.touchAction = 'none';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !draggedElement) return;

    e.preventDefault();
    const { element } = draggedElement;
    const x = e.clientX - dragOffset.x;
    const y = e.clientY - dragOffset.y;

    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !draggedElement) return;

    e.preventDefault();

    const activeId = touchIdentifierRef.current;

    let touch = null;
    if (activeId != null) {
      for (let i = 0; i < e.touches.length; i++) {
        if (e.touches[i].identifier === activeId) {
          touch = e.touches[i];
          break;
        }
      }
    } else if (e.touches.length > 0) {
      // Fallback to the first touch if no active identifier is set
      touch = e.touches[0];
    }

    if (!touch) return;

    const { element } = draggedElement;
    const x = touch.clientX - dragOffset.x;
    const y = touch.clientY - dragOffset.y;

    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  };


  const handleDragEnd = (e) => {

    if (!isDragging || !draggedElement) {
      resetDragState();
      return;
    }

    const { element, index } = draggedElement;
    const wordObj = words[index];

    if (!wordObj) {
      resetDragState();
      return;
    }

    const word = wordObj.text;
    const wordType = wordObj.type;

    const dropRect = dropRef.current?.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    const isInsideDrop =
      dropRect &&
      elementRect.left < dropRect.right &&
      elementRect.right > dropRect.left &&
      elementRect.top < dropRect.bottom &&
      elementRect.bottom > dropRect.top;

    if (!isInsideDrop) {
      resetDraggedElement(element);
      // Ensure the cloud flips back correctly after snapping to its slot
      requestAnimationFrame(() => requestAnimationFrame(() => applyCloudTransform()));
      resetDragState();
      return;
    }

    const isWrong = isPortrait
      ? wordType === "positive"
      : positiveWords.includes(word);

    if (isWrong) {
      // Wrong: only mark the cloud (no full-screen red flash)
      element.classList.add("wrong");
      window.setTimeout(() => element.classList.remove("wrong"), 520);
      resetDraggedElement(element);
      // Ensure the cloud flips back correctly after snapping to its slot
      requestAnimationFrame(() => requestAnimationFrame(() => applyCloudTransform()));

      const wrongPts = parseInt(themeData?.wrong_points) || 0;
      if (wrongPts) {
        setPoints((prev) => prev - Math.abs(wrongPts));
        triggerFx({ type: "wrong" }, 520);
      } else {
        triggerFx({ type: "wrong" }, 520);
      }
    }

    else {

      const earnedPoints = parseInt(themeData.points) || 0;

      setPoints(prev => prev + earnedPoints);
      setCounter(prevCounter => {

        const newCounter = prevCounter + 1;

        // ✅ DEMO BYPASS LOGIC
        if (isDemoBypass && newCounter >= 1) {
          console.log("demo check", isDemoBypass);
          console.log("newCounter check", newCounter);

          setTimeout(() => {
            Swal.fire({
              icon: 'info',
              text: 'Thank you for playing. Subscribe to any PLAN to play with your peers.',
              confirmButtonText: 'OK',
              customClass: { popup: 'custom-success-popup' },
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then(result => {
              if (result.isConfirmed) {
                window.location.href =
                  process.env.REACT_APP_BASE_URL + "/plans";
              }
            });
          }, 500);

          return newCounter;
        }

        // ✅ NORMAL GAME LOGIC
        const totalNegative =
          parseInt(themeData?.negtive_upload_count) ||
          (parseInt(themeData?.category_upload_count) -
            parseInt(themeData?.positive_upload_count)) ||
          (themeData?.negative_cat?.length || 0);

        if (newCounter >= totalNegative) {
          setTimeout(() => {
            completeGame();
          }, 500);
        }

        return newCounter;
      });

      const rawSplash = String(themeData?.Correct_ans_text_popup || "").trim();
      // If admin text is the generic "Answer is right", use nicer defaults.
      const splashSource =
        rawSplash && /answer\s+is\s+right/i.test(rawSplash) ? "" : rawSplash;
      const splashWord = pickRandomSplashWord(splashSource);
      const dropCenter = getDropCenter();
      triggerFx(
        {
          type: "correct",
          text: splashWord,
          color: "#FFD54A",
          ...(dropCenter ? { x: dropCenter.x, y: dropCenter.y } : {})
        },
        3000
      );

      // Burst/pop the cloud before removing it
      element.classList.add("burst");
      element.style.pointerEvents = "none";

      setTimeout(() => {
        if (isPortrait) {
          // Replace this same slot with the next NEGATIVE word (keeps 3+3 on screen)
          const nextNeg = portraitPoolRef.current?.negative?.shift();
          setWords((prev) => {
            const copy = Array.isArray(prev) ? [...prev] : new Array(6).fill(null);
            if (nextNeg) copy[index] = nextNeg;
            else copy[index] = null;
            // keep fixed 6 slots for stable placement
            if (copy.length < 6) {
              while (copy.length < 6) copy.push(null);
            }
            return copy.slice(0, 6);
          });
        } else {
          element.style.display = "none";
          setWords((prev) => prev.filter((w) => w.id !== wordObj.id));
        }
      }, 420);

      const totalNegative =
        parseInt(themeData?.negtive_upload_count) || 0;

      if (counter + 1 === totalNegative) {
        setTimeout(() => completeGame(), 500);
      }
    }

    resetDragState();
  };


  const resetDraggedElement = (element) => {
    if (!element) return;
    element.classList.remove('dragging');
    element.style.position = '';
    element.style.zIndex = '';
    element.style.left = '';
    element.style.top = '';
    element.style.transform = '';
    element.style.transformOrigin = '';
    element.style.cursor = '';
    element.style.display = '';
    element.style.touchAction = '';

    // Restore the original ID-based positioning
    const id = element.id;
    if (id) {
      element.style.left = '';
      element.style.top = '';
    }

    // Unlock flip after it has snapped back (avoid visible flip during snap)
    window.setTimeout(() => {
      if (!element) return;
      delete element.dataset.flipLock;
      applyCloudTransform();
    }, 120);
  };

  const resetDragState = () => {
    setIsDragging(false);
    setDraggedElement(null);
    setDragOffset({ x: 0, y: 0 });
    touchIdentifierRef.current = null;
  };

  // Event Listeners - FIXED with non-passive touch events
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleDragEnd);

      // CRITICAL FIX: Add touch event listeners with { passive: false }
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd, { passive: false });
      document.addEventListener('touchcancel', handleDragEnd, { passive: false });
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleDragEnd);
      document.removeEventListener('touchcancel', handleDragEnd);
    };
  }, [isDragging, draggedElement, dragOffset]);

  // Also fix the touchstart on the document to prevent scrolling
  useEffect(() => {
    // Prevent touch scrolling on the document when dragging starts
    const preventTouchScroll = (e) => {
      if (isDragging) {
        e.preventDefault();
      }
    };

    if (isDragging) {
      document.addEventListener('touchstart', preventTouchScroll, { passive: false });
    }

    return () => {
      document.removeEventListener('touchstart', preventTouchScroll);
    };
  }, [isDragging]);

  // Prevent body scroll when dragging on mobile
  useEffect(() => {
    if (isDragging) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isDragging]);

  if (!themeData) return null;

  const iconColor =
    themeData?.icon_color ||
    themeData?.landing_page_title_color ||
    "#ffffff";

  return (
    <>
      <div
        className={[
          "game-container",
          fx?.type === "wrong" ? "shake" : "",
          fx?.type === "wrong" ? "wrong-glow" : "",
          fx?.type === "correct" ? "correct-glow" : ""
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {fx?.type === "correct" ? (
          <div
            key={`fx-text-${fx.nonce}`}
            className="fx-text"
            aria-live="polite"
            style={{
              color: fx?.color || "#FFD54A",
              ...(fx?.x != null ? { "--fx-x": `${fx.x}px` } : {}),
              ...(fx?.y != null ? { "--fx-y": `${fx.y}px` } : {})
            }}
          >
            {fx.text}
          </div>
        ) : null}

        <header className="game-hud-bar" aria-label="Game status">
          <div className="game-hud-left">
            <div
              className="game-hud-time"
              style={{ color: iconColor }}
            >
              <span className="game-hud-icon" aria-hidden="true">
                <svg
                  className="game-hud-icon-svg"
                  viewBox="0 0 24 24"
                  width="28"
                  height="28"
                  focusable="false"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="13" r="8" />
                  <path d="M9 2h6" />
                  <path d="M12 13l3 2" />
                  <path d="M12 5v2" />
                </svg>
              </span>
              <span id="timer" className="game-hud-time-value">
                {formatTime()}
              </span>
            </div>

            <button
              type="button"
              className="game-hud-rules"
              onClick={openHowToPlay}
              aria-label="How to play"
              title="How to play"
              style={{ color: iconColor }}
            >
              <span className="game-hud-rules-icon" aria-hidden="true">
                ?
              </span>
            </button>
          </div>

          <div
            className="game-hud-title-box"
            style={{
              "--game-hud-title-box-bg": themeData?.button_color
                ? themeData.button_color
                : "rgba(255, 255, 255, 0.95)"
            }}
          >
            <h3
              className="game-hud-title-text"
              style={{
                color: themeData?.landing_page_title_color || "#111111"
              }}
            >
              {themeData?.landing_page_title}
            </h3>
          </div>

          <div
            className="game-hud-points"
            style={{ color: iconColor }}
          >
            <span className="game-hud-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="28" height="28" focusable="false">
                <path
                  fill="currentColor"
                  d="M12 17.3 6.2 20.6l1.5-6.7L2.5 9.8l6.8-.6L12 2.8l2.7 6.4 6.8.6-5.2 4.1 1.5 6.7L12 17.3Z"
                />
              </svg>
            </span>
            <span id="score" className="game-hud-points-value">
              {points}
            </span>
          </div>
        </header>

        <div className="bg-container">
          <img
            ref={bgRef}
            src={
              S3_URL +
              (isPortrait
                ? themeData.mobile_all_icon_image || themeData.all_icon_image
                : themeData.all_icon_image)
            }
            className="bg-image"
            alt="background"
          />

          <div className="item_box">
            {words.map((wordObj, index) => {
              if (!wordObj) return null;
              return (
                <div
                  key={wordObj.id}
                  id={`drag${index}`}
                  className="items drag"
                  ref={el => dragRefs.current[wordObj.id] = el}
                  onMouseDown={(e) => handleMouseDown(e, index)}
                  onTouchStart={(e) => handleTouchStart(e, index)}
                  style={{
                    animationDelay: `${-(index % 8) * 0.25}s`
                  }}
                >
                  <img
                    ref={el => imageRefs.current[wordObj.id] = el}
                    src={S3_URL + themeData.cloud_image}
                    alt="cloud"
                    draggable="false"
                  />
                  <span
                    className="cloud-word"
                    ref={el => textRefs.current[wordObj.id] = el}
                    style={{
                      color: themeData?.card_color_text || "#000"
                    }}
                  >
                    {wordObj.text
                      ? wordObj.text.charAt(0).toUpperCase() + wordObj.text.slice(1).toLowerCase()
                      : ""}
                  </span>

                </div>
              );
            })}

            <div
              className="items drop"
              ref={dropRef}
            >
              <img
                src={S3_URL + themeData.dreamcatchter_image}
                alt="dreamcatcher"
                draggable="false"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DreamcatcherGame;